import React, { useEffect, useState } from "react";
import { FiClock } from "react-icons/fi";
import api from "../../api/axios";
import { useCompany } from "../../context/CompanyContext";
import { useCurrentUser } from "../../utilities/currentUser";
import { CHART_RENDERERS, KPI_KEYS, LIST_RENDERERS, renderKpi } from "./leadershipSections";
import { leadershipTitle } from "./leadershipTitle";
import "./LeadershipDashboard.css";

/**
 * Shared dashboard for Chairman, MD, Executive Director and Investor (Admins preview every card).
 * GET /api/leadership/overview answers { sections, visible } for the company in X-Company-Id; the page
 * renders exactly the cards listed in `visible`, so the API alone decides what a role sees.
 */
const LeadershipDashboard = () => {
  const { user } = useCurrentUser();
  const { currentCompany, currentCompanyId } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [months, setMonths] = useState(6);

  // Re-fetch when the company switches: the API scopes every figure to X-Company-Id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/api/leadership/overview", { params: { months } });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Leadership dashboard failed", err);
        if (!cancelled) {
          setError("Failed to load the leadership dashboard");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [months, currentCompanyId]);

  const visible = Array.isArray(data?.visible) ? data.visible : [];
  const sections = data?.sections || {};
  const shown = (keys) => keys.filter((key) => visible.includes(key) && sections[key] !== undefined);

  const kpis = shown(KPI_KEYS);
  const charts = shown(Object.keys(CHART_RENDERERS));
  const lists = shown(Object.keys(LIST_RENDERERS));
  const companyName = currentCompany?.company?.name;

  return (
    <div className="ld-page">
      <div className="ld-header">
        <div>
          <h1>{leadershipTitle(user)}</h1>
          <p>Live figures{companyName ? ` for ${companyName}` : ""}</p>
        </div>
        <div className="ld-header-date">
          <FiClock />
          <span>
            Last updated: {data?.as_of ? new Date(data.as_of).toLocaleDateString() : new Date().toLocaleDateString()}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      {loading && !data && <p className="ld-muted">Loading…</p>}

      {kpis.length > 0 && (
        <div className="ld-group" data-testid="ld-key-figures">
          <h2 className="ld-group-title">Key Figures</h2>
          <div className="ld-kpi-grid">{kpis.map((key) => renderKpi(key, sections[key]))}</div>
        </div>
      )}

      {charts.length > 0 && (
        <div className="ld-group">
          <h2 className="ld-group-title">Trends and Inventory</h2>
          <div className="ld-grid">
            {charts.map((key) => CHART_RENDERERS[key](sections[key], { months, onMonthsChange: setMonths }))}
          </div>
        </div>
      )}

      {lists.length > 0 && (
        <div className="ld-group">
          <h2 className="ld-group-title">Details</h2>
          <div className="ld-grid">{lists.map((key) => LIST_RENDERERS[key](sections[key]))}</div>
        </div>
      )}

      {!loading && !error && data && visible.length === 0 && (
        <p className="ld-muted">There is nothing to show for your roles in this company.</p>
      )}
    </div>
  );
};

export default LeadershipDashboard;
